import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../mock';
import { TrendingUp, Package, Award, Shield, Target, ArrowRight, Globe, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Home = () => {
  const { t } = useLanguage();

  const stats = [
    { label: t.about.stat1Label, value: t.about.stat1 },
    { label: t.about.stat2Label, value: t.about.stat2 },
    { label: t.about.stat3Label, value: t.about.stat3 },
  ];

  const featuredServices = [
    {
      icon: TrendingUp,
      title: t.services.service1.title,
      description: t.services.service1.description,
    },
    {
      icon: Package,
      title: t.services.service2.title,
      description: t.services.service2.description,
    },
  ];

  const whyUsReasons = [
    { icon: Shield, title: t.whyUs.reason1.title, description: t.whyUs.reason1.description },
    { icon: Award, title: t.whyUs.reason2.title, description: t.whyUs.reason2.description },
    { icon: Target, title: t.whyUs.reason3.title, description: t.whyUs.reason3.description },
    { icon: Globe, title: t.whyUs.reason4.title, description: t.whyUs.reason4.description },
    { icon: Handshake, title: t.whyUs.reason5.title, description: t.whyUs.reason5.description },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center md:items-end justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1761307234324-0c9eadb951de"
            alt="Global Trading"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#003840]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pb-20 md:pb-32 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-float">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8 leading-relaxed animate-fade-in">
              {t.hero.subtitle}
            </p>
            <div className="flex justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-[#007369] hover:bg-[#005A5B] text-white px-10 py-7 text-lg shadow-2xl">
                  {t.contact.title}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#003840] mb-6">{t.about.title}</h2>
              <h3 className="text-2xl font-semibold text-[#007369] mb-4">{t.about.companyName}</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">{t.about.description1}</p>
              <p className="text-gray-600 mb-6 leading-relaxed">{t.about.description2}</p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-2 sm:p-4 bg-[#F4F7F6] rounded-lg overflow-hidden">
                    <div className="text-base sm:text-2xl font-bold text-[#007369] mb-1 whitespace-nowrap">{stat.value}</div>
                    <div className="text-[10px] sm:text-sm text-gray-600 leading-[1.2] break-words hyphens-auto">{stat.label}</div>
                  </div>
                ))}
              </div>

              <p className="text-gray-600 leading-relaxed">{t.about.description3}</p>
            </div>

            <div className="relative">
              <img
                src={images.about}
                alt="ARIA MEA Operations"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#008C72]/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services Preview */}
      <section className="py-20 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003840] mb-4">{t.offer.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.offer.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {featuredServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-[#F4F7F6] rounded-lg">
                      <Icon className="h-6 w-6 text-[#007369]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#003840]">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" className="bg-[#007369] hover:bg-[#005A5B] text-white px-8 py-6 text-lg group">
                {t.whyWorkWithUs.viewAllServices}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003840] mb-4">{t.whyUs.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.whyWorkWithUs.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {whyUsReasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-[#F4F7F6] rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-3 bg-white rounded-lg inline-block mb-4">
                    <Icon className="h-8 w-8 text-[#007369]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#003840] mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-[#003840] text-white p-8 rounded-2xl text-center">
            <p className="text-lg leading-relaxed max-w-4xl mx-auto">{t.whyUs.closing}</p>
          </div>
        </div>
      </section>

      {/* Regional Presence */}
      <section className="py-20 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">{t.globalReach.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.globalReach.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                region: t.globalReach.middleEast.title,
                description: t.globalReach.middleEast.description
              },
              {
                region: t.globalReach.asia.title,
                description: t.globalReach.asia.description
              },
              {
                region: t.globalReach.europeAfrica.title,
                description: t.globalReach.europeAfrica.description
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <h3 className="text-2xl font-bold text-[#007369] mb-4">{item.region}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;